import { BRANDS_CATEGORY_ID } from "@faire/help-center-utils/consts/BRANDS_CATEGORY_ID";
import { CATEGORY_ID } from "@faire/help-center-utils/consts/CATEGORY_ID";
import { RETAILERS_CATEGORY_ID } from "@faire/help-center-utils/consts/RETAILERS_CATEGORY_ID";
import { CustomerTypes } from "@faire/help-center-utils/enums/CustomerType";
import { useAboutFaireArticles } from "@faire/help-center-utils/hooks/article/useAboutFaireArticles";
import { sortByOrderList } from "@faire/help-center-utils/lib/arrayFormatter";
import { getLatestDataUpdatedAt } from "@faire/help-center-utils/lib/getLatestDataUpdatedAt";
import { localeString } from "@faire/help-center-utils/lib/localeString";
import {
  categoryPlaceholder,
  guidesPlaceHolder,
  popularGuidesPlaceHolder,
  sectionsPlaceHolder,
} from "@faire/help-center-utils/lib/random";
import { toArticleWithPreview } from "@faire/help-center-utils/lib/topics";
import { getBrand } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import {
  ICategory,
  IGuide,
  ISection,
} from "@faire/help-center-utils/types/zendesk";
import { isNotUndefined } from "@faire/typescript";
import { useQuery as useZendeskCategories } from "@faire/web-api--source/endpoints/www/api/zendesk/locale/categories/get-hooks";
import { useQuery as useZendeskSections } from "@faire/web-api--source/endpoints/www/api/zendesk/locale/sections/get-hooks";
import { IZendeskArticle } from "@faire/web-api--source/com/faire/vendor/zendesk/IZendeskArticle";
import { IZendeskSection } from "@faire/web-api--source/com/faire/vendor/zendesk/IZendeskSection";
import { useMemo } from "react";

import { isHiddenSection } from "../consts";

import { useCustomerType } from "@faire/help-center-utils/hooks/useCustomerType";
import { useAllCategoryArticles } from "./useAllCategoryArticles";
import { useAllSectionArticles } from "./useAllSectionArticles";

export const useSectionStore = () => {
  const {
    data: categoriesResponse,
    isFetching: isCategoriesFetching,
    dataUpdatedAt: categoriesDataUpdatedAt,
  } = useZendeskCategories(localeString);
  const {
    data: sectionsResponse,
    isFetching: isSectionsFetching,
    dataUpdatedAt: sectionsDataUpdatedAt,
  } = useZendeskSections(localeString);

  const categories = categoriesResponse?.categories ?? [];
  const sections = useMemo(() => {
    return (
      sectionsResponse?.sections.filter((section: IZendeskSection) => {
        return !isHiddenSection(section.id);
      }) ?? []
    );
  }, [sectionsResponse]);
  const { customerType } = useCustomerType();
  const {
    categoriesArticlesResponses,
    isCategoriesArticlesLoading,
    categoriesArticlesDataUpdatedAt,
  } = useAllCategoryArticles(categories);
  const {
    sectionArticlesResponses,
    isSectionArticlesLoading,
    sectionArticlesDataUpdatedAt,
  } = useAllSectionArticles(sections);

  const categoriesArticles = categories
    ? (categories.map((category, index) => {
        const articles =
          categoriesArticlesResponses?.[index]?.data?.articles || [];
        const number_of_articles =
          articles.filter((article) => !article.draft).length || 0;
        return {
          ...category,
          articles,
          number_of_articles,
        };
      }) as ICategory[])
    : [];
  const sectionsArticles = sections
    ? (sections.map((section, index) => {
        const articles =
          sectionArticlesResponses?.[index]?.data?.articles || [];
        const number_of_articles =
          articles.filter((article) => !article.draft).length || 0;

        return {
          ...section,
          articles,
          number_of_articles,
          page: sectionArticlesResponses?.[index]?.data?.page,
          per_page: sectionArticlesResponses?.[index]?.data?.per_page,
          page_count: sectionArticlesResponses?.[index]?.data?.page_count,
          count: sectionArticlesResponses?.[index]?.data?.count,
        };
      }) as ISection[])
    : [];
  const isLoading =
    isCategoriesFetching ||
    isSectionsFetching ||
    isCategoriesArticlesLoading ||
    isSectionArticlesLoading;

  const dataUpdatedAt = getLatestDataUpdatedAt([
    categoriesDataUpdatedAt,
    sectionsDataUpdatedAt,
    categoriesArticlesDataUpdatedAt,
    sectionArticlesDataUpdatedAt,
  ]);
  const loggedBrandType = getBrand() ? CustomerTypes.BRAND : undefined;
  const loggedUserType = getRetailer()
    ? CustomerTypes.RETAILER
    : loggedBrandType;

  const getCategory = (customerType: CustomerTypes): ICategory => {
    if (isLoading || !categories) {
      return categoryPlaceholder as ICategory;
    }
    const selectedCategory = categoriesArticles.find(
      (category: ICategory) => category.id === CATEGORY_ID[customerType]
    )!;
    const articles = selectedCategory?.articles
      ?.slice(0, 6)
      .filter((article) => !article.draft);
    const sectionIds = sectionsArticles
      .map((section) => section.id)
      .filter(isNotUndefined);
    return {
      ...selectedCategory,
      articles: articles?.sort(sortByOrderList("section_id", sectionIds)),
    };
  };

  const retailerPopularGuides = getCategory(CustomerTypes.RETAILER);
  const brandPopularGuides = getCategory(CustomerTypes.BRAND);

  const getPopularGuides = () => {
    if (isLoading) {
      return popularGuidesPlaceHolder as IZendeskArticle[];
    }

    return categoriesArticles?.find(
      (category: ICategory) => category.id === CATEGORY_ID[customerType]
    )?.articles;
  };

  const categoryId =
    customerType === CustomerTypes.BRAND
      ? BRANDS_CATEGORY_ID
      : RETAILERS_CATEGORY_ID;

  const sectionsWithArticles = sectionsArticles
    ?.map((section) => {
      const articles = section?.articles
        ?.filter((article) => !article.draft)
        .slice(0, 3);
      if (articles?.length === 0) {
        return undefined;
      }
      return {
        ...section,
        articles,
      };
    })
    .filter(isNotUndefined);

  const allTopics =
    isLoading || !customerType
      ? (sectionsPlaceHolder() as ISection[])
      : sectionsWithArticles?.filter(
          (section: ISection) => section.category_id === categoryId
        ) || [];

  const getGuides = (
    selectedGuides: { name: string; id: string; articleSection?: string }[]
  ) => {
    if (isLoading) {
      return guidesPlaceHolder as IGuide[];
    }
    const selectedGuidesWithPreview = selectedGuides.map(
      (selectedGuide: {
        name: string;
        id: string;
        articleSection?: string;
      }) => {
        const selectedSection = sectionsArticles.find((section: ISection) =>
          section.articles?.some(
            (article) => article.id === Number(selectedGuide.id)
          )
        );
        const selected =
          selectedSection?.articles?.find(
            (article: IZendeskArticle) =>
              article.id === Number(selectedGuide.id)
          ) || ({} as IZendeskArticle);
        return toArticleWithPreview(selected, selectedSection?.name);
      }
    );
    return selectedGuidesWithPreview;
  };

  const aboutFaireArticles = useAboutFaireArticles();
  const aboutFaireSection = getGuides(aboutFaireArticles);

  return {
    isLoading,
    categories: categoriesArticles,
    sections: sectionsArticles,
    loggedUserType,
    retailerPopularGuides,
    brandPopularGuides,
    getPopularGuides,
    getGuides,
    aboutFaireSection,
    allTopics,
    dataUpdatedAt,
  };
};
